import { motion } from "framer-motion";
import { Check } from "lucide-react";

const CHAPTERS = [
  {
    num: "01",
    title: "THE COMPLETE IPO LIFECYCLE",
    body: "YIIS-100 — IPO Intelligence Edition manages the entire lifecycle of IPO activity: from account setup and application recording through allotment, listing, profit/loss analysis and reporting. One desktop suite, every stage covered.",
    tags: ["IPO ACCOUNTS", "APPLICATIONS", "ALLOTMENT & LISTING"],
  },
  {
    num: "02",
    title: "INTELLIGENCE THAT COMPOUNDS",
    body: "Allotment rate, IPO success rate, portfolio ROI and capital blocked — computed live across every account you manage. Performance highlights automatically surface your best IPO, best account and best month.",
    tags: ["PORTFOLIO ANALYTICS", "ROI ENGINE", "MONTHLY INTELLIGENCE"],
  },
  {
    num: "03",
    title: "OPERATION BLUE",
    body: "v1.0 — codename Operation BLUE — is the first desktop production release of the IPO Intelligence Edition. YIIS-100 is the flagship module line of the YKD Investment Intelligence Suite, with new modules already on the YKD Enterprises roadmap.",
    tags: ["V1.0 PRODUCTION", "YKD PRODUCT LINE", "MORE MODULES COMING"],
  },
];

const MODULES = [
  "IPO Account Management",
  "IPO Application Management",
  "Allotment & Listing Management",
  "Realized & Unrealized P/L Tracking",
  "IPO Analytics & Performance Intelligence",
  "Executive, Account-wise, IPO-wise, Monthly & Yearly Reports",
  "TXT, CSV & JSON Report Export",
  "Database Backup & Restore",
  "Application Settings",
  "Light & Dark Themes",
  "License & Product Information",
  "Windows Desktop GUI",
];

export const Manifesto = () => (
  <>
    <section id="manifesto" className="px-6 md:px-12 max-w-[1400px] mx-auto py-24 md:py-32" data-testid="manifesto-section">
      <div className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-16 md:mb-24">
        The Manifesto
      </div>
      <div className="flex flex-col gap-24 md:gap-32">
        {CHAPTERS.map((ch, idx) => (
          <motion.div
            key={ch.num}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className={`grid grid-cols-1 md:grid-cols-12 gap-8 items-start ${idx % 2 === 1 ? "md:text-right" : ""}`}
            data-testid={`manifesto-chapter-${ch.num}`}
          >
            <div className={`md:col-span-4 ${idx % 2 === 1 ? "md:order-2" : ""}`}>
              <span className="font-display font-semibold text-7xl md:text-8xl text-stroke leading-none">
                {ch.num}
              </span>
            </div>
            <div className={`md:col-span-8 ${idx % 2 === 1 ? "md:order-1" : ""}`}>
              <h2 className="font-display font-semibold tracking-normal [word-spacing:0.12em] text-3xl md:text-5xl mb-6">
                {ch.title}
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-2xl md:max-w-none md:ml-0">
                {ch.body}
              </p>
              <div className={`flex flex-wrap gap-3 mt-8 ${idx % 2 === 1 ? "md:justify-end" : ""}`}>
                {ch.tags.map((t) => (
                  <span
                    key={t}
                    className="font-mono text-[10px] tracking-[0.2em] uppercase border border-border px-3 py-1.5 text-muted-foreground hover:border-accent hover:text-accent transition-colors duration-300"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>

    <section id="modules" className="px-6 md:px-12 max-w-[1400px] mx-auto pb-24 md:pb-32" data-testid="modules-section">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <h2 className="font-display font-semibold tracking-normal [word-spacing:0.12em] text-3xl md:text-5xl" data-testid="modules-heading">
            V1.0 PRODUCTION <span className="text-accent">SCOPE</span>
          </h2>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">
            12 modules — shipping in Operation Blue
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {MODULES.map((m, i) => (
            <motion.div
              key={m}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="group bg-background p-6 md:p-7 hover:bg-muted/40 transition-colors duration-300"
              data-testid={`module-cell-${i + 1}`}
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <Check size={15} className="text-accent" />
              </div>
              <div className="font-display font-medium text-base md:text-lg leading-snug group-hover:text-accent transition-colors duration-300">
                {m}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  </>
);
